"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactDetails = [{
  icon: Mail,
  title: "Email Us",
  value: "portlandwellnessspallc@\ngmail.com"
}, {
  icon: Phone,
  title: "Call Us",
  value: "(503) 890-9303"
}, {
  icon: MapPin,
  title: "Visit Our Sanctuary",
  value: "8948 SW Barbur Blvd, Portland, OR 97219"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 bg-secondary/30 border-2 border-border text-center h-full min-h-[400px] rounded-lg">
        <CircleCheck className="w-16 h-16 text-primary mb-6" />
        <h3 className="text-2xl font-bold font-default mb-2 text-primary">Message Received</h3>
        <p className="text-muted-foreground font-sans mb-8">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg border-2 border-primary h-14 px-8 font-bold hover:bg-secondary text-primary">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-bold font-default text-base text-primary-foreground">Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Your name" {...field} className="border-2 border-secondary rounded-lg h-14 px-4 bg-background focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent shadow-sm font-sans text-foreground" />
            </FormControl>
            <FormMessage className="font-sans text-destructive-foreground" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-bold font-default text-base text-primary-foreground">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="email@example.com" {...field} className="border-2 border-secondary rounded-lg h-14 px-4 bg-background focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent shadow-sm font-sans text-foreground" />
            </FormControl>
            <FormMessage className="font-sans text-destructive-foreground" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-bold font-default text-base text-primary-foreground">Your Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you find balance today?" {...field} className="border-2 border-secondary rounded-lg min-h-[160px] p-4 bg-background focus-visible:ring-2 focus-visible:ring-accent focus-visible:border-accent shadow-sm font-sans text-foreground resize-none" />
            </FormControl>
            <FormMessage className="font-sans text-destructive-foreground" />
          </FormItem>} />

      <FormField control={control} name="service" render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-bold font-default text-base text-primary-foreground">Service</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="border-2 border-secondary rounded-lg h-14 px-4 bg-background focus:ring-2 focus:ring-accent focus:border-accent shadow-sm font-sans text-foreground">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-background border-2 border-secondary rounded-lg">
                <SelectItem value="body-treatments">Body Treatments</SelectItem>
                <SelectItem value="foot-treatments">Foot Treatments</SelectItem>
                <SelectItem value="combo-services">Combo Services</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="font-sans text-destructive-foreground" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive font-sans rounded-lg">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-lg bg-chart-2 hover:bg-chart-2/90 text-white h-14 text-lg font-bold font-sans shadow-md transition-transform hover:scale-[1.02]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 w-5 h-5" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function ContactSection() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252249" id="connect-with-us" className="bg-background py-24 md:py-32">
      <div className="container mx-auto px-2.5 md:px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          
          <div ref={leftRef} className="flex flex-col justify-center transition-all duration-1000 ease-out opacity-100 translate-y-0 !w-[350px]">
            <div className="mb-12">
              <h2 className="text-4xl md:text-5xl font-bold font-default mb-6 text-primary">
                Connect With Us
              </h2>
              <p className="text-lg text-foreground font-sans max-w-md leading-relaxed">Not sure where to start? Our staff will work with you to find the right treatment for what your body needs. Just come in and let us take it from there.</p>
            </div>

            <div className="space-y-10 flex flex-col items-center">
              {contactDetails.map((detail, index) => {
              const Icon = detail.icon;
              return <div key={index} data-index={index} className="flex items-start gap-6">
                    <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center shrink-0 shadow-sm">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold font-default text-primary mb-1">
                        {detail.title}
                      </h3>
                      <p className="text-foreground font-sans text-lg !w-[250px]">
                        {detail.value}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div ref={rightRef} className={`transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-primary p-8 md:p-12 rounded-3xl shadow-xl">
              <FormProvider formKey="connect_form_contact" formId="connect_form_contact" sectionName="Contact Section" defaultValues={{
              name: "",
              email: "",
              message: "",
              service: ""
            }} submitText="Send Message" successMessage="Thank you for reaching out. We will get back to you shortly.">
                <ContactFormInner />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}